import React from 'react';
import SettingsIcon from 'material-ui/svg-icons/action/settings';
import './PlanSettingsIcon.styl';
import ModifyReviewDialogContainer from 'components/coaching/ModifyReviewDialogContainer';
import isTodayOrInThePast from 'utils/datetime/isTodayOrInThePast';
import confirm from 'utils/confirm';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import noop from 'lodash/noop';
import PlanSettingsMenu from 'components/coaching/landing-pages/helpers/buttons/PlanSettingsMenu';
import AuthStore from 'stores/AuthStore';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import withCoachingPlanWizard from 'components/coaching/wizard/withCoachingPlanWizard';
import InProgressReviewActions from 'actions/coaching/InProgressReviewActions';

const endPlanWarning = 'Are you sure you want to end this coaching plan?';

interface Props {
  plan: any; // TODO
  review: any; // TODO
  rep: any; // TODO
  refreshCoachingData: () => void;
  onEditPlan: (plan, refreshCoachingData) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  /**
   * Slight hack - do you want to hide the cog? Necessary because having the Modify dialog
   * embedded in this component necessitates that this component stays rendered
   */
  hide: boolean;
  /**
   * The dialog pulls double duty - is this more focused on a review or a plan?
   */
  reviewFocused: boolean;

  /**
   * Notify parent(s) that upcoming reviews need to be updated.
   */
  refreshUpcomingReviews: () => void;
}

interface State {
  showMeetingDialog: boolean;
}

class PlanSettingsIcon extends React.PureComponent<Props, State> {
  constructor(props) {
    super(props);

    this.openMeetingDialog = this.openMeetingDialog.bind(this);
    this.closeMeetingDialog = this.closeMeetingDialog.bind(this);
    this.onEndPlan = this.onEndPlan.bind(this);
    this.onEditPlan = this.onEditPlan.bind(this);
    this.onDeleteReview = this.onDeleteReview.bind(this);

    this.state = {showMeetingDialog: false};
  }

  openMeetingDialog() {
    this.setState({showMeetingDialog: true});
  }

  closeMeetingDialog() {
    this.setState({showMeetingDialog: false});
  }

  onEndPlan() {
    confirm(endPlanWarning).then(async () => {
      const {refreshCoachingData, plan, refreshUpcomingReviews, review} = this.props;
      await CoachingPlanActions.endPlan(plan.id);
      refreshCoachingData && refreshCoachingData();
      refreshUpcomingReviews && refreshUpcomingReviews();

      // If ad-hoc, remove from InProgressReview store.
      if (plan.adHoc) {
        const authUser = AuthStore.getState().user;
        InProgressReviewActions.deleteInProgressReview(review.id, authUser);
      }
    }, noop);
  }

  /**
   * onEditPlan is provided by `withCoachingPlanWizard`
   */
  onEditPlan() {
    const {plan, onEditPlan, refreshCoachingData} = this.props;
    onEditPlan(plan, refreshCoachingData);
  }

  /**
   * Edit review functionality is provided inside the modify dialog container
   */
  onDeleteReview() {
    const {review, refreshCoachingData} = this.props;
    confirm('Are you sure you want to delete this meeting?').then(() => {
      CoachingReviewActions.deleteReview(review.id).then(
        () => refreshCoachingData && refreshCoachingData()
      );
    }, noop);
  }

  render() {
    const {plan, review, rep, reviewFocused, refreshCoachingData, onMenuOpen, onMenuClose, hide} =
      this.props;

    if (!plan || !review) return null;

    const authUser = (AuthStore as any).getState().user;

    const isPlanCoach = plan.coach.id === authUser.id;
    const isReviewCoach = review && review.coachUserId === authUser.id;
    const isAdmin = authUser.isAdmin();

    const canStartReview = review && isTodayOrInThePast(review.scheduleAt) && isReviewCoach;

    if (canStartReview || isAdmin || isPlanCoach) {
      return (
        <div className="plan-settings-wrapper">
          {!hide && (
            <GenericTooltip
              clickBehavior
              blurToClose={false}
              className="plan-settings-tooltip"
              onOpen={onMenuOpen}
              onClose={onMenuClose}
              tooltip={
                <PlanSettingsMenu
                  endPlan={this.onEndPlan}
                  editPlan={this.onEditPlan}
                  editMeeting={this.openMeetingDialog}
                  canStartReview={canStartReview}
                  canEditPlan={isPlanCoach || isAdmin}
                  plan={plan}
                  review={review}
                  rep={rep}
                  reviewFocused={reviewFocused}
                />
              }>
              <SettingsIcon className="plan-settings-icon edit-icon" />
            </GenericTooltip>
          )}
          <ModifyReviewDialogContainer
            open={this.state.showMeetingDialog}
            refreshCoachingData={refreshCoachingData}
            onRequestClose={this.closeMeetingDialog}
            review={review}
            refreshUpcomingReviews={this.props.refreshUpcomingReviews}
          />
        </div>
      );
    } else {
      return null;
    }
  }
}

export default withCoachingPlanWizard(PlanSettingsIcon);
